import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  User,
} from 'firebase/auth';
import {
  deleteField,
  doc,
  getDoc,
  onSnapshot,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { auth, db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { AuthState, Role, UserProfile } from './types';
import {
  hydrateStoresFromUserState,
  flushUserState,
  setCurrentUid,
  initializeLocalSession,
  saveLocalSession,
} from '../services/userState';
import { trackRead, trackWrite, setUsageTrackingUser, flushUsageToFirestore } from '../services/firebaseUsageTracker';
import { checkAndInvalidateCache } from '../services/cacheInvalidation';

type SignupInput = { email: string; password: string; token: string };
type SelfServeSignupInput = { email: string; password: string };

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  signupWithInvite: (input: SignupInput) => Promise<void>;
  signupSelfServe: (input: SelfServeSignupInput) => Promise<void>;
  signInWithGoogle: () => Promise<boolean>;
  impersonate: (targetUid: string) => Promise<void>;
  clearImpersonation: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function mapProfile(uid: string, data: Record<string, unknown> | undefined, fallbackEmail: string | null): UserProfile {
  const allowedRoles: Role[] = ['user', 'dev', 'admin'];
  const incomingRoles =
    Array.isArray(data?.roles) && data?.roles.length ? (data.roles as unknown[]) : ['user'];
  let roles = incomingRoles.filter((r): r is Role => allowedRoles.includes(r as Role));

  // Ensure at least 'user' role
  if (!roles.length) roles = ['user'];

  // Note: Role protection is enforced server-side via Firestore security rules
  // Users cannot modify their own roles; only devs can change roles

  return {
    uid,
    email: (data?.email as string | undefined) ?? fallbackEmail,
    roles,
    impersonating: (data?.impersonating as string | null | undefined) ?? null,
    invitedBy: (data?.invitedBy as string | null | undefined) ?? null,
    disabled: (data?.disabled as boolean | undefined) ?? false,
    casinoName: (data?.casinoName as string | undefined),
    createdAt: data?.createdAt as Timestamp | undefined,
    // Billing / entitlement fields — written only by the Stripe webhook (Admin SDK).
    plan: (data?.plan as UserProfile['plan']) ?? 'free',
    subscriptionStatus: (data?.subscriptionStatus as UserProfile['subscriptionStatus']) ?? null,
    stripeCustomerId: (data?.stripeCustomerId as string | null | undefined) ?? null,
    stripeSubscriptionId: (data?.stripeSubscriptionId as string | null | undefined) ?? null,
    billingInterval: (data?.billingInterval as UserProfile['billingInterval']) ?? null,
    currentPeriodEnd: (data?.currentPeriodEnd as Timestamp | null | undefined) ?? null,
    cancelAtPeriodEnd: (data?.cancelAtPeriodEnd as boolean | undefined) ?? false,
    planSource: (data?.planSource as UserProfile['planSource']) ?? 'self',
  };
}

export const AuthProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [effectiveProfile, setEffectiveProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const profileUnsubRef = useRef<(() => void) | undefined>(undefined);
  const currentUidRef = useRef<string | null>(null);
  const provisionAttemptedRef = useRef<boolean>(false);

  // Initialize local session on mount (device-specific state like bankroll, deck, gameMode)
  // so the user sees their local state immediately, before Firebase hydration. The dev
  // cache-invalidation check is deferred to sign-in (below): config/app is only readable
  // by authenticated users, so reading it here (pre-auth) throws a permission error.
  useEffect(() => {
    initializeLocalSession();
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, next => {
      setFirebaseUser(next);
      setLoading(false);
      // Now that we know who's signed in, check whether a dev has remotely invalidated
      // caches (config/app requires auth to read). If so, the stale localStorage was just
      // cleared, so reload the local session to pick up the reset. Best-effort, non-blocking.
      if (next) {
        checkAndInvalidateCache()
          .then(({ wasInvalidated }) => {
            if (wasInvalidated) initializeLocalSession();
          })
          .catch(() => {
            /* non-fatal: cache check is best-effort */
          });
      }
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (profileUnsubRef.current) {
      profileUnsubRef.current();
    }

    if (!firebaseUser) {
      currentUidRef.current = null;
      setCurrentUid(null);
      setUsageTrackingUser(null);
      setProfile(null);
      setEffectiveProfile(null);
      return;
    }

    currentUidRef.current = firebaseUser.uid;
    provisionAttemptedRef.current = false;
    setCurrentUid(firebaseUser.uid);

    // Track if hydration has happened for this user session
    let hasHydrated = false;

    setLoading(true);
    const profileRef = doc(db, 'users', firebaseUser.uid);

    profileUnsubRef.current = onSnapshot(
      profileRef,
      async snap => {
        // Track the profile read
        trackRead('users', 'profile-listener');

        if (!snap.exists()) {
          // Self-serve/Google sign-ins create the auth user before the profile
          // doc exists. Provision it once via the server (rules deny client
          // creation); onSnapshot re-fires when the doc lands. Only surface an
          // error if it's still missing after one attempt. Call the callable
          // inline (not via a component-scope helper) so this effect's
          // dependency array stays `[firebaseUser]` — `functions` is a stable
          // import, so there's no exhaustive-deps warning.
          if (!provisionAttemptedRef.current) {
            provisionAttemptedRef.current = true;
            httpsCallable(functions, 'ensureUserProfile')().catch((err) => {
              console.warn('ensureUserProfile failed', err);
              setError('We couldn’t finish setting up your account. Please try signing in again.');
              setLoading(false);
            });
            return; // stay in loading; the snapshot will re-deliver
          }
          console.error('User document missing for uid:', firebaseUser.uid);
          setError('We couldn’t load your account. Please try signing in again, or contact support.');
          setLoading(false);
          return;
        }

        const nextProfile = mapProfile(firebaseUser.uid, snap.data(), firebaseUser.email);
        setProfile(nextProfile);
        setUsageTrackingUser(firebaseUser.uid);

        // Hydrate stores from Firestore once on login (after we know invitedBy)
        // Pass invitedBy so admin's protected settings (leveling, achievements) propagate
        if (!hasHydrated) {
          hasHydrated = true;
          hydrateStoresFromUserState(firebaseUser.uid, nextProfile.invitedBy)
            .catch((err) => {
              console.warn('Failed to hydrate user state', err);
            });
        }

        // Fetch admin's branding for profile display if user was invited by an admin
        if (nextProfile.invitedBy && !nextProfile.casinoName) {
          try {
            const adminSnap = await getDoc(doc(db, 'users', nextProfile.invitedBy));
            trackRead('users', 'admin-branding');
            if (adminSnap.exists()) {
              const adminData = adminSnap.data();
              if (adminData?.casinoName) {
                // Apply admin's branding to this user's effective profile
                nextProfile.casinoName = adminData.casinoName;
              }
            }
          } catch {
            // Ignore - use default branding
          }
        }

        // B2B: inherit Pro from the inviting admin's team plan. adminConfigs is
        // readable by invitees; the webhook sets orgPlan to 'pro' only while the
        // admin's team subscription is active, so this grants/revokes seat access.
        if (nextProfile.invitedBy) {
          try {
            const cfgSnap = await getDoc(doc(db, 'adminConfigs', nextProfile.invitedBy));
            trackRead('adminConfigs', 'org-entitlement');
            if (cfgSnap.exists()) {
              const cfg = cfgSnap.data();
              nextProfile.orgPlanActive = cfg?.orgPlan === 'pro';
              nextProfile.orgSeatLimit = (cfg?.seatLimit as number | undefined) ?? null;
            }
          } catch {
            // Ignore — default to no inherited entitlement.
          }
        }

        if (nextProfile.impersonating) {
          const targetSnap = await getDoc(doc(db, 'users', nextProfile.impersonating));
          trackRead('users', 'impersonate-target');
          if (targetSnap.exists()) {
            setEffectiveProfile(mapProfile(targetSnap.id, targetSnap.data(), nextProfile.email));
          } else {
            setEffectiveProfile(nextProfile);
          }
        } else {
          setEffectiveProfile(nextProfile);
        }

        setError(null);
        setLoading(false);
      },
      err => {
        setError(err.message);
        setLoading(false);
      },
    );

    return () => {
      if (profileUnsubRef.current) {
        profileUnsubRef.current();
      }
    };
  }, [firebaseUser]);

  // Flush before unload and on visibility change (tab switch, minimize)
  useEffect(() => {
    const beforeUnloadHandler = async () => {
      // Save local session immediately (synchronous localStorage)
      saveLocalSession();
      // Try to flush Firebase (may not complete)
      if (currentUidRef.current) {
        await flushUserState(currentUidRef.current);
      }
    };

    const visibilityHandler = () => {
      if (document.hidden) {
        // User left the tab - save local session immediately
        saveLocalSession();
        // Also try to flush Firebase
        if (currentUidRef.current) {
          flushUserState(currentUidRef.current);
        }
      }
    };

    window.addEventListener('beforeunload', beforeUnloadHandler);
    document.addEventListener('visibilitychange', visibilityHandler);
    
    return () => {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
      document.removeEventListener('visibilitychange', visibilityHandler);
    };
  }, []);

  const roles = useMemo(() => profile?.roles ?? [], [profile]);

  const signInWithGoogle = async (): Promise<boolean> => {
    setError(null);
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      // ensureUserProfile is idempotent; its `created` flag tells us whether this
      // was a brand-new account (so the caller can show onboarding).
      const res = await httpsCallable(functions, 'ensureUserProfile')();
      trackWrite('users', 'google-signup');
      setLoading(false);
      return Boolean((res.data as { created?: boolean })?.created);
    } catch (err) {
      setLoading(false);
      setError(err instanceof Error ? err.message : 'Google sign-in failed');
      throw err;
    }
  };

  const login = async (email: string, password: string) => {
    setError(null);
    await signInWithEmailAndPassword(auth, email, password);
  };

  const logout = async () => {
    if (currentUidRef.current) {
      await flushUserState(currentUidRef.current);
    }
    setCurrentUid(null);
    flushUsageToFirestore(); // Ensure usage stats are saved before logout
    setUsageTrackingUser(null);
    await signOut(auth);
    setProfile(null);
    setEffectiveProfile(null);
  };

  const signupWithInvite = async ({ email, password, token }: SignupInput) => {
    setError(null);
    setLoading(true);

    try {
      // Call secure Cloud Function for signup
      // This handles invite validation, user creation, and role assignment server-side
      const createUserFn = httpsCallable<
        { email: string; password: string; token: string },
        { success: boolean; uid: string; email: string; roles: string[] }
      >(functions, 'createUserWithInvite');

      await createUserFn({ email: email.toLowerCase(), password, token });
      
      // Sign in with email/password after successful account creation
      await signInWithEmailAndPassword(auth, email.toLowerCase(), password);
      
      trackWrite('users', 'signup-create-via-function');
      setLoading(false);
    } catch (err: unknown) {
      setLoading(false);
      // Extract error message from Firebase Functions error
      const error = err as { message?: string; code?: string };
      throw new Error(error.message || 'Signup failed. Please try again.');
    }
  };

  const signupSelfServe = async ({ email, password }: SelfServeSignupInput) => {
    setError(null);
    setLoading(true);
    try {
      // Public signup (no invite). Creates the auth user + free-plan user doc
      // server-side, then signs in. Gated by OPEN_SIGNUP_ENABLED on the function.
      const createUserFn = httpsCallable<
        { email: string; password: string },
        { success: boolean; uid: string; email: string; roles: string[] }
      >(functions, 'createUserSelfServe');

      await createUserFn({ email: email.toLowerCase(), password });
      await signInWithEmailAndPassword(auth, email.toLowerCase(), password);
      trackWrite('users', 'signup-self-serve-via-function');
      setLoading(false);
    } catch (err: unknown) {
      setLoading(false);
      const error = err as { message?: string; code?: string };
      throw new Error(error.message || 'Signup failed. Please try again.');
    }
  };

  const impersonate = async (targetUid: string) => {
    if (!profile) throw new Error('Not signed in');
    if (!profile.roles.includes('dev') && !profile.roles.includes('admin')) {
      throw new Error('Insufficient permissions to impersonate');
    }
    await updateDoc(doc(db, 'users', profile.uid), { impersonating: targetUid });
    trackWrite('users', 'impersonate-set');
  };

  const clearImpersonation = async () => {
    if (!profile) return;
    await updateDoc(doc(db, 'users', profile.uid), { impersonating: deleteField() });
    trackWrite('users', 'impersonate-clear');
  };

  const refreshProfile = async () => {
    if (!firebaseUser) return;
    const snap = await getDoc(doc(db, 'users', firebaseUser.uid));
    trackRead('users', 'profile-refresh');
    if (snap.exists()) {
      setProfile(mapProfile(firebaseUser.uid, snap.data(), firebaseUser.email));
    }
  };

  const value: AuthContextValue = {
    user: firebaseUser,
    profile,
    effectiveProfile,
    roles,
    loading,
    error,
    login,
    logout,
    signupWithInvite,
    signupSelfServe,
    signInWithGoogle,
    impersonate,
    clearImpersonation,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return ctx;
}

